import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import ContentContainer from "../../templates/content-container/contentContainer";
import { PAGE_IDS } from "../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../app/data/common/queryKeys";
import PaymentService from "../../app/data/payment/paymentService";
import { PaymentDetail, PublicPaymentLinkSummary } from "../../app/data/payment/PaymentSession";
import { UserUtils } from "../../app/data/user/userUtils";
import UserState from "../../slices/user/UserState";
import { userSelector } from "../../slices/user/userSlice";
import Button, { ButtonSizes, ButtonThemes } from "../../ui-components/button/button";
import { DataTable } from "../../ui-components/data-table";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import LabeledTextInput from "../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import LabeledDateRangeInput from "../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "../../ui-components/molecules/labeled-inputs/labeledInput";
import { useDataTableColumnPreferences } from "../../hooks/useDataTableColumnPreferences";
import { copyTextToClipboard } from "../../hooks/utils";
import ProbillChipInput from "../shipments/labels-receipts/probillChipInput";
import { formatProbillDisplay } from "../shipments/labels-receipts/types";
import { PUBLIC_PAY_MAX_INVOICES, usePayableProbills } from "../public-payment/usePayableProbills";
import PaymentDetailSidebar from "../payments/admin-browser/paymentDetailSidebar";
import PublicPaymentLinkDetail from "./publicPaymentLinkDetail";
import {
  getPublicPaymentLinkColumns,
  LINK_COLUMN_OPTIONS,
  LINK_COLUMNS_HIDDEN_BY_DEFAULT,
} from "./publicPaymentLinkColumns";
import "../shipments/labels-receipts/labelsReceipts.scss";
import "../payments/admin-browser/paymentAdminBrowser.scss";
import "./publicPaymentLinks.scss";

const paymentService = PaymentService.getInstance();
const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "ACTIVE", label: "Active" },
  { value: "PAID", label: "Paid" },
  { value: "EXPIRED", label: "Expired" },
  { value: "REVOKED", label: "Revoked" },
];

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 10000 }),
};

const numericFilter = (value: string): number | undefined =>
  /^\d+$/.test(value) ? Number(value) : undefined;

const toApiDate = (value: string): string | undefined => {
  if (!value) return undefined;
  const formatted = value.toApiDateFormat();
  return formatted && formatted !== "Invalid date" ? formatted : undefined;
};

const errorText = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const PublicPaymentLinks: React.FC = () => {
  const queryClient = useQueryClient();
  const userState: UserState = useSelector(userSelector);
  const isAdmin = UserUtils.isXGSAdministrator(userState.profile);
  const { entries, setEntries, inputValue, setInputValue, groups, lookupError, validating } =
    usePayableProbills();
  const [email, setEmail] = useState("");
  const [created, setCreated] = useState<PublicPaymentLinkSummary[]>([]);
  const [splitAcrossAccounts, setSplitAcrossAccounts] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [probillFilter, setProbillFilter] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [createdBy, setCreatedBy] = useState("");

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PublicPaymentLinkSummary | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [paymentDetailId, setPaymentDetailId] = useState<string | null>(null);
  const [paymentDetail, setPaymentDetail] = useState<PaymentDetail | null>(null);
  const [paymentDetailError, setPaymentDetailError] = useState<string | null>(null);
  const [paymentDetailLoading, setPaymentDetailLoading] = useState(false);

  const filters = useMemo(
    () => ({
      status: status || undefined,
      email: emailFilter.trim() || undefined,
      probillNumber: numericFilter(probillFilter),
      accountNumber: numericFilter(accountFilter),
      dateFrom: toApiDate(dateFrom),
      dateTo: toApiDate(dateTo),
      createdBy: isAdmin ? createdBy.trim() || undefined : undefined,
    }),
    [accountFilter, createdBy, dateFrom, dateTo, emailFilter, isAdmin, probillFilter, status]
  );

  const linksQuery = useInfiniteQuery({
    queryKey: [QUERY_KEYS.PUBLIC_PAYMENT_LINKS, filters],
    queryFn: async ({ pageParam }) => {
      const response = await paymentService.searchPublicPaymentLinks({
        ...filters,
        cursor: pageParam,
        size: PAGE_SIZE,
      });
      if (!response.ok() || !response.data) {
        const err = response.getError?.();
        throw new Error(typeof err === "string" ? err : "Unable to load Quick Pay links.");
      }
      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage?.nextCursor || undefined,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const rows = useMemo(
    () => linksQuery.data?.pages.flatMap((page) => page.items || []) ?? [],
    [linksQuery.data]
  );

  const refreshLinks = () => {
    queryClient.invalidateQueries([QUERY_KEYS.PUBLIC_PAYMENT_LINKS]);
  };

  const validNumbers = entries
    .filter((entry) => entry.status === "valid" && entry.value != null)
    .map((entry) => entry.value as number);

  const onCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validNumbers.length || validating) {
      setFormError("Add at least one probill with an open amount.");
      return;
    }
    if (!EMAIL_PATTERN.test(email.trim())) {
      setFormError("Enter a valid email address.");
      return;
    }
    setSaving(true);
    setFormError(null);
    setCreated([]);
    setSplitAcrossAccounts(false);
    const response = await paymentService.createPublicPaymentLink({
      probillNumbers: validNumbers,
      email: email.trim(),
    });
    setSaving(false);
    if (!response.ok() || !response.data) {
      const err = response.getError?.();
      setFormError(typeof err === "string" ? err : "Unable to create the payment link.");
      return;
    }
    setCreated(response.data.links || []);
    setSplitAcrossAccounts(!!response.data.splitAcrossAccounts);
    setEntries([]);
    setInputValue("");
    setEmail("");
    refreshLinks();
  };

  const closeLinkDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setDetailError(null);
    setPaymentDetailId(null);
    setPaymentDetail(null);
    setPaymentDetailError(null);
    setPaymentDetailLoading(false);
  };

  const openPaymentDetail = async (paymentId: string) => {
    setPaymentDetailId(paymentId);
    setPaymentDetail(null);
    setPaymentDetailError(null);
    setPaymentDetailLoading(true);
    const response = await paymentService.getPaymentDetail(paymentId);
    setPaymentDetailLoading(false);
    if (response.ok() && response.data) {
      setPaymentDetail(response.data);
      return;
    }
    const err = response.getError?.();
    setPaymentDetailError(typeof err === "string" ? err : "Unable to load this payment.");
  };

  const onRevoke = async (link: PublicPaymentLinkSummary) => {
    setActionError(null);
    const response = await paymentService.revokePublicPaymentLink(link.id);
    refreshLinks();
    if (selectedId === link.id) {
      openDetail(link.id);
    }
    if (!response.ok()) {
      const err = response.getError?.();
      setActionError(typeof err === "string" ? err : "Unable to revoke that link.");
    }
  };

  const openDetail = async (id: string) => {
    setSelectedId(id);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    const response = await paymentService.getPublicPaymentLinkDetail(id);
    setDetailLoading(false);
    if (response.ok() && response.data) {
      setDetail(response.data);
      return;
    }
    const err = response.getError?.();
    setDetailError(typeof err === "string" ? err : "Unable to load this link.");
  };

  const columns = useMemo(
    () => getPublicPaymentLinkColumns((link) => copyTextToClipboard(link.url), onRevoke),
    // onRevoke closes over selectedId; recreate when the open row changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedId]
  );
  const columnPrefs = useDataTableColumnPreferences({
    instanceKey: "quick-pay-links",
    columnOptions: LINK_COLUMN_OPTIONS,
    hiddenByDefault: LINK_COLUMNS_HIDDEN_BY_DEFAULT,
  });
  const statusOption = STATUS_OPTIONS.find((option) => option.value === status) ?? STATUS_OPTIONS[0];

  return (
    <ContentContainer title="Quick Pay Links" pageId={PAGE_IDS.PUBLIC_PAYMENT_LINKS}>
      <div className="xgs-public-payment-links">
        <form className="xgs-public-payment-links__composer" onSubmit={onCreate}>
          <div className="xgs-public-payment-links__composer-head">
            <div>
              <h2 className="xgs-public-payment-links__composer-title">Create pay link</h2>
              <p className="xgs-public-payment-links__composer-desc">
                Lock XGS probill numbers and send a link the customer can pay without signing in.
                They can change the email before they pay.
              </p>
            </div>
          </div>
          {(formError || lookupError) && (
            <XGSErrorMessage>{formError || lookupError}</XGSErrorMessage>
          )}
          <div className="xgs-public-payment-links__composer-body">
            <label className="xgs-public-payment-links__label">XGS probill numbers</label>
            <ProbillChipInput
              entries={entries}
              onEntriesChange={setEntries}
              inputValue={inputValue}
              onInputValueChange={setInputValue}
              maxValid={PUBLIC_PAY_MAX_INVOICES}
              emptyPlaceholder="Type or paste XGS probill numbers…"
              addPlaceholder="Add another XGS probill number…"
              invalidFormatMessage="Enter an XGS probill number"
              capNoun="XGS probill numbers"
            />
            <label className="xgs-public-payment-links__label" htmlFor="public-link-email">
              Email
            </label>
            <input
              id="public-link-email"
              className="xgs-public-payment-links__input"
              type="email"
              placeholder="customer@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Button
              theme={ButtonThemes.blue}
              size={ButtonSizes.autoMedium}
              type="submit"
              spinner={saving}
              disabled={saving || validating || !validNumbers.length}
            >
              {groups.length > 1 ? "Create links" : "Create link"}
            </Button>
          </div>
          {groups.length > 1 && (
            <p className="xgs-public-payment-links__note">
              These invoices are billed to different accounts. Creating the link will make one
              link for each account.
            </p>
          )}
          {created.length > 0 && (
            <div className="xgs-public-payment-links__ready">
              <div className="xgs-public-payment-links__ready-title">
                {created.length === 1 ? "Link ready" : "Links ready"}
              </div>
              <p>
                {splitAcrossAccounts
                  ? "These invoices are billed to different accounts, so a separate link was created for each account."
                  : "Share this link with the customer."}
              </p>
              {created.map((link) => (
                <div key={link.id} className="xgs-public-payment-links__ready-row">
                  <div>
                    <div className="xgs-public-payment-links__probills">
                      {(link.probillNumbers || []).map((number) => formatProbillDisplay(number)).join(", ")}
                    </div>
                    <div className="xgs-public-payment-links__url">{link.url}</div>
                  </div>
                  <button
                    type="button"
                    className="xgs-public-payment-links__text-action"
                    onClick={() => copyTextToClipboard(link.url)}
                  >
                    Copy
                  </button>
                </div>
              ))}
            </div>
          )}
        </form>
        <div className="xgs-public-payment-links__filters">
          <LabeledSelectInput
            label="Status"
            labelMode={LabelModes.column}
            className="xgs-public-payment-links__filter"
            options={STATUS_OPTIONS}
            value={statusOption}
            onValueChange={(option) => setStatus(option?.value ?? "")}
            menuPortalTarget={document.body}
            styles={filterSelectStyles}
          />
          <LabeledTextInput
            label="Email"
            labelMode={LabelModes.column}
            className="xgs-public-payment-links__filter"
            value={emailFilter}
            onChange={(event) => setEmailFilter(event.target.value)}
          />
          <LabeledTextInput
            label="XGS probill number"
            labelMode={LabelModes.column}
            className="xgs-public-payment-links__filter"
            value={probillFilter}
            onChange={(event) => setProbillFilter(event.target.value)}
          />
          <LabeledTextInput
            label="Account"
            labelMode={LabelModes.column}
            className="xgs-public-payment-links__filter"
            value={accountFilter}
            onChange={(event) => setAccountFilter(event.target.value)}
          />
          <LabeledDateRangeInput
            label="Created"
            labelMode={LabelModes.column}
            className="xgs-public-payment-links__filter xgs-public-payment-links__filter--dates"
            start={dateFrom}
            end={dateTo}
            onStartChange={setDateFrom}
            onEndChange={setDateTo}
          />
          {isAdmin && (
            <LabeledTextInput
              label="Created by"
              labelMode={LabelModes.column}
              className="xgs-public-payment-links__filter"
              value={createdBy}
              onChange={(event) => setCreatedBy(event.target.value)}
            />
          )}
          <Button theme={ButtonThemes.blue} type="button" onClick={() => linksQuery.refetch()}>
            Search
          </Button>
        </div>
        {(actionError || linksQuery.isError) && (
          <XGSErrorMessage>
            {actionError || errorText(linksQuery.error, "Unable to load Quick Pay links.")}
          </XGSErrorMessage>
        )}
        <DataTable
          isLoading={linksQuery.isLoading || (linksQuery.isFetching && !linksQuery.isFetchingNextPage)}
          keepTableOnLoading
          columns={columns}
          data={rows}
          rowHeight={56}
          cursorPointer
          onRowClicked={(row: PublicPaymentLinkSummary) => openDetail(row.id)}
          ignoreCellClick="actions"
          responsive
          sorting
          enableRowSelection={false}
          {...columnPrefs}
          showColumnSelector
          columnSelectorOptions={LINK_COLUMN_OPTIONS}
          enableColumnResize
          enableColumnReorder
          stickyHeaders
          noResultsText="No Quick Pay links found"
          infiniteScroll
          infiniteScrollHasNext={!!linksQuery.hasNextPage}
          infiniteScrollLoading={linksQuery.isFetchingNextPage}
          onInfiniteScroll={() => {
            if (linksQuery.hasNextPage && !linksQuery.isFetchingNextPage) {
              linksQuery.fetchNextPage();
            }
          }}
        />
      </div>
      <PublicPaymentLinkDetail
        show={!!selectedId}
        isLoading={detailLoading}
        link={detail}
        error={detailError}
        onClose={closeLinkDetail}
        onViewPayment={openPaymentDetail}
      />
      <PaymentDetailSidebar
        show={!!paymentDetailId}
        isLoading={paymentDetailLoading}
        detail={paymentDetail}
        detailError={paymentDetailError}
        onClose={() => {
          setPaymentDetailId(null);
          setPaymentDetail(null);
          setPaymentDetailError(null);
        }}
      />
    </ContentContainer>
  );
};

export default PublicPaymentLinks;
